import React, { createContext, useContext, useState, useEffect } from "react";
import { translations } from "../utils/translations";
import { storage, STORAGE_KEYS } from "../utils/storage";
import ApiService from "../services/ApiService.js";

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      "useLocalization must be used within a LocalizationProvider",
    );
  }
  return context;
};

export const LocalizationProvider = ({ children }) => {
  // Set Ukrainian as the default language
  const [currentLanguage, setCurrentLanguage] = useState("uk");

  useEffect(() => {
    loadLanguage();
  }, []);

  const loadLanguage = () => {
    try {
      const savedLanguage = storage.getItem(STORAGE_KEYS.LANGUAGE);

      if (savedLanguage && translations[savedLanguage]) {
        // Use saved language if it exists and is valid
        setCurrentLanguage(savedLanguage);
      } else {
        // Default to Ukrainian and save it
        setCurrentLanguage("uk");
        storage.setItem(STORAGE_KEYS.LANGUAGE, "uk");
      }
    } catch (error) {
      console.error("Error loading language:", error);
      // Always fallback to Ukrainian on error
      setCurrentLanguage("uk");
      try {
        storage.setItem(STORAGE_KEYS.LANGUAGE, "uk");
      } catch (storageError) {
        console.error("Error saving default language:", storageError);
      }
    }
  };

  const changeLanguage = async (language) => {
    try {
      if (translations[language]) {
        // If no auth token yet (e.g., right after registration), save locally only
        const token = storage.getItem(STORAGE_KEYS.TOKEN);
        if (token) {
          // Make API request to update user language when authenticated
          await ApiService.patch("users", { language });
        }

        // Update local state and storage
        setCurrentLanguage(language);
        storage.setItem(STORAGE_KEYS.LANGUAGE, language);

        return { success: true };
      } else {
        console.warn(`Language ${language} is not supported`);
        return {
          success: false,
          error: `Language ${language} is not supported`,
        };
      }
    } catch (error) {
      // Fallback: on 401 or network errors, save language locally so UI stays consistent
      if (error?.response?.status === 401 || !error?.response) {
        try {
          setCurrentLanguage(language);
          storage.setItem(STORAGE_KEYS.LANGUAGE, language);
          return { success: true, localOnly: true };
        } catch (storageError) {
          console.error("Error saving language locally after failure:", storageError);
        }
      }
      console.error("Error changing language:", error);
      return { success: false, error: error.response?.data?.detail || error?.message || "Failed to change language" };
    }
  };

  // Local-only language setter (no backend call)
  const changeLanguageLocal = (language) => {
    try {
      if (translations[language]) {
        setCurrentLanguage(language);
        storage.setItem(STORAGE_KEYS.LANGUAGE, language);
        return { success: true };
      } else {
        console.warn(`Language ${language} is not supported`);
        return {
          success: false,
          error: `Language ${language} is not supported`,
        };
      }
    } catch (error) {
      console.error("Error changing language locally:", error);
      return { success: false, error: "Failed to change language locally" };
    }
  };

  const t = (key, params = {}, language = null) => {
    try {
      const lang = language || currentLanguage;
      let translation = translations[lang]?.[key] || translations[currentLanguage]?.[key] || key;

      // Support for parameters in translations
      Object.keys(params).forEach((param) => {
        translation = translation.replace(`{{${param}}}`, params[param]);
      });

      return translation;
    } catch (error) {
      console.error("Error getting translation:", error);
      return key;
    }
  };

  const getAvailableLanguages = () => {
    const languageNames = {
      uk: "Українська",
      en: "English",
      pl: "Polski",
      ru: "Русский",
      es: "Español",
      pt: "Português",
    };

    return Object.keys(translations).map((code) => ({
      code,
      name: languageNames[code] || code,
      nativeName: languageNames[code] || code,
    }));
  };

  const getCurrentLanguageInfo = () => {
    const languages = getAvailableLanguages();
    return (
      languages.find((lang) => lang.code === currentLanguage) ||
      languages.find((lang) => lang.code === "uk") ||
      languages[0]
    );
  };

  const value = {
    currentLanguage,
    changeLanguage,
    changeLanguageLocal,
    t,
    translations: translations[currentLanguage] || translations["uk"] || {},
    availableLanguages: getAvailableLanguages(),
    currentLanguageInfo: getCurrentLanguageInfo(),
    getAvailableLanguages,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
